import { useMutation, useQueryClient } from '@tanstack/react-query';
import ConfirmModal from 'components/modals/ConfirmModal';
import { CloseModal } from 'components/modals/types';
import Toast from 'components/toasts/Toast';
import { TypographyWithThemeColor } from 'components/TypographyWithThemeColor';
import { getRouteUrl } from 'config/routes';
import { forcedLogout } from 'features/customer/store/actions';
import { IntlMessageKeys } from 'features/i18n/IntlMessageKeys';
import { LottieLoadingAnimation } from 'features/loading-spinner/LottieLoadingAnimation';
import { ModalBodyWithColumnDirectionAndSpaceBetween } from 'features/theme-2024/ModalWithDividedHeaderLayout';
import { useDeleteWorkspaceMutation } from 'features/workspaces/useDeleteWorkspaceMutation';
import {
  invalidateWorkspaceInfoQuery,
  useGetWorkspaceInfo,
} from 'features/workspaces/useGetWorkspaceInfo';
import { setWorkspaceId } from 'features/workspaces/workspaceSlice';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import { httpDeleteCustomer } from 'services/backofficeIntegration/http/endpoints/customer/httpDeleteCustomer';
import { httpCustomGetWorkspaceInfo } from 'services/backofficeIntegration/http/endpoints/workspace/httpCustomGetWorkspaceInfo';
import {
  invalidateGetAllCustomerWorkspaceIdsQuery,
  useGetAllCustomerWorkspaceIds,
} from 'services/backofficeIntegration/http/endpoints/workspace/useGetAllCustomerWorkspaceIds';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';

const useDeleteWorkspaceAndSwitchMutation = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutateAsync: deleteWorkspace } = useDeleteWorkspaceMutation();
  const { data: workspaceIds } = useGetAllCustomerWorkspaceIds();

  return useMutation(async (workspaceId: string) => {
    await deleteWorkspace(workspaceId);

    invalidateWorkspaceInfoQuery(workspaceId);
    invalidateGetAllCustomerWorkspaceIdsQuery();

    // If the customer is assigned to more workspaces, switch to the next one
    if (workspaceIds && workspaceIds.length > 1) {
      const nextWorkspaceId = workspaceIds.find(id => id !== workspaceId);
      if (nextWorkspaceId) {
        const newWorkspaceInfo = await httpCustomGetWorkspaceInfo.callEndpoint({
          workspaceId: nextWorkspaceId,
        });

        dispatch(setWorkspaceId(nextWorkspaceId));

        // Invalidate all query caches
        queryClient.clear();

        Toast.success('workspace.toast.delete_workspace_and_switched_confirm.success', {
          name: newWorkspaceInfo.name,
        });
        return true;
      }
    }

    // No further workspace available, delete the customer
    await httpDeleteCustomer.callEndpoint();

    // Logout
    dispatch(forcedLogout);
    navigate(getRouteUrl('login'));
  });
};

type Props = CloseModal & {
  workspaceId: string;
  workspaceName: string;
};

export const DeleteWorkspaceAndAccountConfirmModal = ({
  closeModal,
  workspaceId,
  workspaceName,
}: Props) => {
  const { mutateAsync: deleteWorkspaceAndSwitch, isPending: isDeleting } =
    useDeleteWorkspaceAndSwitchMutation();

  const { data: workspaceIds, isLoading: isLoadingWorkspaceIds } = useGetAllCustomerWorkspaceIds();

  // Find another workspace this customer is part of
  const nextWorkspaceId = workspaceIds?.find(id => id !== workspaceId);

  // If the customer has another workspace, get the name of it for showing in UI
  const { data: nextWorkspaceInfo } = useGetWorkspaceInfo({
    targetWorkspaceId: nextWorkspaceId,
    enabled: !!nextWorkspaceId,
  });

  const isLoadingState = isLoadingWorkspaceIds || isDeleting;

  const bodyMessageId: IntlMessageKeys = nextWorkspaceId
    ? 'workspace.modal.delete_workspace_and_switch_confirm.message'
    : 'workspace.modal.delete_workspace_and_account_confirm.message';

  const titleMessageId: IntlMessageKeys = nextWorkspaceId
    ? 'workspace.modal.delete_workspace_and_switch_confirm.title'
    : 'workspace.modal.delete_workspace_and_account_confirm.title';

  return (
    <ConfirmModal
      message={isLoadingState ? undefined : bodyMessageId}
      messageValues={{
        br: <br />,
        name: workspaceName,
        newName: nextWorkspaceInfo?.name,
      }}
      cancelText="common.cancel"
      okText="common.delete"
      onCancel={closeModal}
      onOK={async () => {
        await deleteWorkspaceAndSwitch(workspaceId);

        closeModal();
      }}
      slots={{
        bodyContainer: CustomModalBody,
      }}
    >
      {isLoadingState ? (
        <AnimationContainer>
          <LottieLoadingAnimation animation="circle2" />
        </AnimationContainer>
      ) : (
        <TypographyWithThemeColor variant="h5">
          <FormattedMessage id={titleMessageId} />
        </TypographyWithThemeColor>
      )}
    </ConfirmModal>
  );
};

const CustomModalBody = styled(ModalBodyWithColumnDirectionAndSpaceBetween)({
  paddingTop: 0,
});

const AnimationContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
